import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Check, CircleAlert, CircleCheck, Map as MapIcon, Newspaper, Send } from 'lucide-react';
import { apiErrorMessage, publicService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Brand, Button, Field, InfinityMark } from '../components/ui';
import './Auth.css';
import './AdminUsers.css';

const USERNAME = /^[a-z0-9._-]{3,60}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Pedido de cadastro, sem login. O admin revisa em Usuários › Solicitações. */
function RequestAccess() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [trails, setTrails] = useState([]);
  const [form, setForm] = useState({ name: '', username: '', email: '', courseIds: [], trailIds: [], articlesOnly: false, message: '' });
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    publicService.courses().then(setCourses).catch(() => setCourses([]));
    publicService.trails().then(setTrails).catch(() => setTrails([]));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const toggle = (key, id) => setForm(f => ({
    ...f,
    [key]: f[key].includes(id) ? f[key].filter(c => c !== id) : [...f[key], id],
  }));

  const valid = form.name.trim() && USERNAME.test(form.username) && EMAIL.test(form.email.trim())
    && (form.courseIds.length > 0 || form.trailIds.length > 0 || form.articlesOnly);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!USERNAME.test(form.username)) {
      setError('O login deve ter de 3 a 60 caracteres: letras minúsculas, números, ponto, hífen ou sublinhado.');
      return;
    }
    setSending(true);
    try {
      await publicService.requestAccess({ ...form, name: form.name.trim(), email: form.email.trim() });
      setSent({ username: form.username, email: form.email.trim() });
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível enviar. Verifique sua conexão.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__strip" aria-hidden="true" />
      <InfinityMark className="auth__watermark" strokeWidth={4} />

      <div className="auth__box auth__box--wide">
        <div className="auth__brand">
          <Brand size="md" onDark />
          <span className="auth__slogan">Estudo sem limite.</span>
        </div>

        {sent ? (
          <div className="auth__card">
            <span className="auth__done"><CircleCheck size={28} aria-hidden="true" /></span>
            <h1>Solicitação enviada</h1>
            <p className="auth__lead">Recebemos o pedido de <strong>{sent.username}</strong>.</p>
            <ol className="auth__steps">
              <li className="is-done">
                <span><Check size={15} strokeWidth={3} aria-hidden="true" /></span>
                <span><strong>Pedido enviado</strong><span>Agora mesmo</span></span>
              </li>
              <li>
                <span>2</span>
                <span><strong>Revisão do admin</strong><span>Costuma levar até 1 dia útil</span></span>
              </li>
              <li>
                <span>3</span>
                <span><strong>Você recebe o acesso</strong><span>O admin vai te passar o login e uma senha temporária</span></span>
              </li>
              <li>
                <span>4</span>
                <span><strong>Primeiro acesso</strong><span>Você cria uma senha própria</span></span>
              </li>
            </ol>
            <Button variant="outline" size="lg" block icon={ArrowLeft} onClick={() => navigate('/login')}>Voltar para o login</Button>
          </div>
        ) : (
          <form className="auth__card" onSubmit={submit}>
            <button type="button" className="auth__back" onClick={() => navigate('/login')}>
              <ArrowLeft size={16} aria-hidden="true" />Voltar para o login
            </button>
            <h1>Solicitar acesso</h1>
            <p className="auth__lead">O admin revisa o pedido e cria sua conta. Você recebe o login e uma senha temporária.</p>

            {error && (
              <div className="auth__error" role="alert">
                <CircleAlert size={18} aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <div className="auth__two">
              <Field label="Nome" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" autoFocus required />
              </Field>
              <Field label="Login desejado" required hint="Minúsculas, números, ponto ou hífen">
                <input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />
              </Field>
            </div>
            <Field label="E-mail" required hint="Para o admin entrar em contato com você">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" required />
            </Field>

            <div className="auth__fieldset" role="group" aria-labelledby="ra-what">
              <span id="ra-what" className="auth__fieldset-label">O que você quer usar? <span className="auth__req">*</span></span>
              <span className="auth__fieldset-hint">Cada recurso funciona sozinho. Marque só o que precisar.</span>

              <span className="ra-group"><BookOpen size={15} aria-hidden="true" />Cursos</span>
              {courses.map(c => (
                <label key={c.id} className="au-check">
                  <input type="checkbox" checked={form.courseIds.includes(c.id)} onChange={() => toggle('courseIds', c.id)} />
                  <span><strong>{c.name}</strong></span>
                </label>
              ))}

              {trails.length > 0 && <span className="ra-group"><MapIcon size={15} aria-hidden="true" />Trilhas</span>}
              {trails.map(t => (
                <label key={t.id} className="au-check">
                  <input type="checkbox" checked={form.trailIds.includes(t.id)} onChange={() => toggle('trailIds', t.id)} />
                  <span>
                    <strong>{t.shortTitle || t.title}</strong>
                    <small>{[t.examCode, t.courseIds.length > 0 && 'inclui os cursos que a trilha usa'].filter(Boolean).join(' · ') || 'Caminho guiado até o destino'}</small>
                  </span>
                </label>
              ))}

              <span className="ra-group"><Newspaper size={15} aria-hidden="true" />Artigos</span>
              <label className="au-check">
                <input type="checkbox" checked={form.articlesOnly} onChange={(e) => setForm({ ...form, articlesOnly: e.target.checked })} />
                <span><strong>Artigos</strong><small>Ler, curtir, comentar e escrever. Toda conta já tem; marque se for só isso.</small></span>
              </label>
            </div>

            <Field label="Mensagem para o admin" hint="Opcional. Ajuda o admin a identificar você.">
              <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={1000} />
            </Field>

            <Button type="submit" variant="accent" size="lg" block icon={Send} disabled={sending || !valid}>
              {sending ? 'Enviando...' : 'Enviar solicitação'}
            </Button>
          </form>
        )}

        <p className="auth__foot">
          {sent ? 'Enquanto isso, artigos públicos podem ser lidos sem conta.' : (
            <>Já tem conta? <button type="button" onClick={() => navigate('/login')}>Entrar</button></>
          )}
        </p>
      </div>
    </div>
  );
}

export default RequestAccess;
